<template>
    <SettingsTabs tab-key="other-settings">
        <div class="mb-[98px] mt-[44px]">
            <div
                v-if="globalProps.team.personal_team && globalProps.user.app_type === null || globalProps.user.app_type == 'pro' || globalProps.user.app_type == 'both'"
                class="grid-cols-layout border-b border-gray-300 pb-5 pt-5 lg:grid"
                @submit.prevent="updateNotificationPreferences"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800"
                    >
                        Email notifications
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        Choose which emails you want to receive.
                    </p>
                </div>

                <form
                    class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0"
                >
                    <div
                        class="flex flex-wrap gap-6 border-b border-gray-200 px-6 pb-5 pt-[23px]"
                    >
                        <Toggle v-model="editorComments">
                            <span> New comments and mentions from Editor </span>
                        </Toggle>
                    </div>

                    <div class="flex justify-end">
                        <div class="my-3 pr-6">
                            <AtomicButton
                                type="submit"
                                color="yellow"
                                size="sm"
                                :disabled="loading"
                            >
                                Save changes
                            </AtomicButton>
                        </div>
                    </div>
                </form>
            </div>

            <SubscriptionInvoiceSettings
                :model-value="
                    globalProps.team.features?.recurring_invoice_emails ?? []
                "
                @update:modelValue="updateSubscriptionInvoiceSettings"
            />

            <div
                v-if="globalProps.team.personal_team && isOwner"
                class="grid-cols-layout border-t border-gray-300 pt-5 lg:grid"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800"
                    >
                        Switch app type
                    </h3>
                </div>
                <div class="rounded-md bg-white shadow-card">
                    <div class="border-b border-gray-200 px-6">
                        <p class="pt-6 text-sm leading-normal text-gray-500">
                            You are currently using the
                            <span class="font-medium text-gray-900">{{
                                globalProps.user.app_type === 'plugin'
                                    ? 'Plugin'
                                    : 'PRO'
                            }}</span>
                            version of TOPOL.io.
                        </p>
                        <ul class="mt-3 list-disc pl-5 text-sm leading-normal text-gray-500">
                            <li>
                                <span class="font-medium text-gray-700">Plugin</span>
                                &mdash; White-label editor to integrate into platforms, CRMs, and SaaS products.
                                <a
                                    href="https://topol.io/tariff-plugin"
                                    target="_blank"
                                    class="font-medium text-topol-blue-500 hover:text-topol-blue-600"
                                >Learn more</a>
                            </li>
                            <li class="mt-1">
                                <span class="font-medium text-gray-700">PRO</span>
                                &mdash; Cloud-based email &amp; landing page editor for individuals and teams.
                                <a
                                    href="https://topol.io/tariff-pro"
                                    target="_blank"
                                    class="font-medium text-topol-blue-500 hover:text-topol-blue-600"
                                >Learn more</a>
                            </li>
                        </ul>
                        <div class="mb-6 mt-[23px]">
                            <label class="flex">
                                <CheckboxInput
                                    v-model:checked="switchAppTypeConfirmation"
                                    class="mt-[1px]"
                                    size="md"
                                    name="switchAppTypeConfirmation"
                                />
                                <p
                                    class="ml-1 cursor-pointer select-none text-sm leading-normal text-gray-900"
                                >
                                    Confirm that you want to switch to
                                    <span class="font-medium">{{
                                        globalProps.user.app_type === 'plugin'
                                            ? 'PRO'
                                            : 'Plugin'
                                    }}</span>.
                                    This will change your dashboard and available features.
                                </p>
                            </label>
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <div class="my-3 pr-6">
                            <AtomicButton
                                :disabled="!switchAppTypeConfirmation || switchingAppType"
                                color="red"
                                size="sm"
                                @click="confirmSwitchAppType(
                                    globalProps.user.app_type === 'plugin'
                                        ? 'pro'
                                        : 'plugin'
                                )"
                            >
                                Switch to {{
                                    globalProps.user.app_type === 'plugin'
                                        ? 'PRO'
                                        : 'Plugin'
                                }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </SettingsTabs>
</template>

<script lang="ts" setup>
import SettingsTabs from '../components/SettingsTabs.vue';
import CheckboxInput from '@atomic/Inputs/CheckboxInput.vue';

import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import { usePage } from '@inertiajs/vue3';
import useConfirm from '@composables/useConfirm';
import usePermissions from '@composables/usePermissions';

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const { isOwner } = usePermissions();
const { show: showConfirmation } = useConfirm();

const features = ref(JSON.parse(globalProps.value.user.features ?? '{}'));
const notificationsEnabled = computed(() => {
    if (features.value.comments_notifications_disabled === null) {
        return true;
    } else if (features.value.comments_notifications_disabled === '1') {
        return false;
    } else return true;
});

const loading = ref(false);
const switchingAppType = ref(false);
const switchAppTypeConfirmation = ref(false);

const editorComments = ref(notificationsEnabled.value);

const confirmSwitchAppType = async (type: 'plugin' | 'pro') => {
    const label = type === 'plugin' ? 'Plugin' : 'PRO';

    const confirmed = await showConfirmation({
        header: `Switch to ${label}`,
        message: `Are you sure you want to switch your dashboard to ${label}? This will change your workspace and available features.`,
        type: 'danger',
        confirm: `Switch to ${label}`,
    });

    if (!confirmed) {
        return;
    }

    router.put(
        route('settings.app-type.update'),
        { app_type: type },
        {
            onStart: () => {
                switchingAppType.value = true;
            },
            onFinish: () => {
                switchingAppType.value = false;
            },
            onSuccess: () => {
                switchAppTypeConfirmation.value = false;
                create({
                    title: 'App type updated',
                    text: `Your dashboard has been switched to ${label}.`,
                    type: 'success',
                });
            },
            onError: () => {
                create({
                    title: 'Error',
                    text: 'An error occurred while switching app type.',
                    type: 'error',
                });
            },
        }
    );
};

const updateNotificationPreferences = () => {
    router.put(
        route('settings.features.update'),
        {
            feature: 'comments_notifications_disabled',
            enable: !editorComments.value,
        },
        {
            onStart: () => {
                loading.value = true;
            },
            onFinish: () => {
                loading.value = false;
            },
            onSuccess: () => {
                create({
                    title: 'Notification preferences updated',
                    text: 'Your notification preferences have been updated.',
                    type: 'success',
                });
            },
            onError: () => {
                create({
                    title: 'Error',
                    text: 'An error occurred while updating your notification preferences.',
                    type: 'error',
                });
            },
        }
    );
};

const updateSubscriptionInvoiceSettings = (emails: string[]) => {
    router.put(
        route('settings.recurring-invoice-emails.update'),
        {
            emails: emails,
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: 'Emails updated',
                    type: 'success',
                });
            },
            onError: () => {
                create({
                    title: 'Emails not updated',
                    type: 'error',
                });
            },
        }
    );
};
</script>

<style></style>
